import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * ActionButton — a drop-in replacement for a plain <button> whose onClick does
 * async work. While the promise is pending it shows a spinner, disables itself
 * and keeps its width, so every action gives immediate feedback before its
 * success/error message appears.
 *
 * Usage is identical to <button>: swap the tag and keep the same className.
 */
const ActionButton = ({
  onClick,
  children,
  className = '',
  disabled = false,
  busyLabel,
  type = 'button',
  ...props
}) => {
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const handleClick = useCallback(async (event) => {
    if (busy || disabled || !onClick) return;
    let result;
    try {
      result = onClick(event);
    } catch (err) {
      throw err;
    }
    // Only show the spinner for genuinely async handlers.
    if (!result || typeof result.then !== 'function') return;
    setBusy(true);
    try {
      await result;
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [busy, disabled, onClick]);

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={`${className} ${busy ? 'cursor-wait opacity-80' : ''} disabled:opacity-60 disabled:cursor-not-allowed`.trim()}
      {...props}
    >
      {busy && (
        <span
          aria-hidden="true"
          className="inline-block w-3 h-3 mr-1.5 -mt-0.5 align-middle rounded-full border-2 border-current border-t-transparent animate-spin"
        />
      )}
      {busy && busyLabel ? busyLabel : children}
    </button>
  );
};

export default ActionButton;
